import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { LogOutIcon } from "lucide-react";

import { invalidateServerAuthGateState } from "../../environments/primary/auth";
import { ensureInferenceCredential } from "../../environments/primary/inferenceCredential";
import { signOut, useSession } from "../../shared/auth";
import { openAfterAccountChange } from "../../shared/auth/web-address";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Button } from "../ui/button";
import { runSignOut } from "./AccountSettings.logic";
import { SettingsRow, SettingsSection } from "./settingsLayout";
import { useAccountTeam } from "./TeamSettings";

/**
 * Ends the Dovod session from inside the app.
 *
 * Signing out is the only way a user can hand the machine to someone else, or
 * move it to another account, without the previous account's `dvk_` still
 * sitting in the server. The socket it authorized is left to the server's own
 * connection sweep: platform-api reports the session gone within one probe
 * cycle, which is the same path a revocation takes and the only one the server
 * treats as authoritative.
 */
export function AccountSettingsSection() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: session } = useSession();
  const team = useAccountTeam();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const handleSignOut = async () => {
    setIsSigningOut(true);
    setFailure(null);
    const outcome = await runSignOut({
      signOut: () => signOut(),
      restoreInferenceCredential: async () => {
        if (session?.user.id !== undefined) await ensureInferenceCredential(session.user.id);
      },
      invalidateGateState: invalidateServerAuthGateState,
      navigateToSignIn: () => {
        openAfterAccountChange("/sign-in", (to) => void navigate({ to, replace: true }));
      },
    });
    setConfirmOpen(false);
    if (!outcome.ok) {
      setFailure(outcome.message);
      setIsSigningOut(false);
    }
    // On success the button stays disabled: the navigation replaces this
    // surface, and re-enabling it would offer a second sign-out on a session
    // that has already ended.
  };

  const user = session?.user;

  return (
    <SettingsSection
      title={t("settings/account_teeon5")}
      footer={
        <div className="flex items-center justify-end gap-3">
          {failure ? (
            <p className="min-w-0 flex-1 text-[11px] text-muted-foreground">{failure}</p>
          ) : null}
          <Button
            size="xs"
            variant="outline"
            className="text-destructive-foreground"
            disabled={isSigningOut}
            onClick={() => {
              setConfirmOpen(true);
            }}
          >
            <LogOutIcon className="size-3.5 text-destructive-foreground" />
            {isSigningOut ? t("settings/signingOut_be1th7") : t("settings/signOutOfAccount_blpkm3")}
          </Button>
        </div>
      }
    >
      {user?.name ? (
        <SettingsRow
          title={t("common/name_p0qla7")}
          description={null}
          control={
            <span className="w-full text-right text-[13px] text-foreground">{user.name}</span>
          }
        />
      ) : null}
      <SettingsRow
        title={t("settings/email_65374f")}
        description={null}
        control={
          <span className="w-full text-right text-[13px] text-foreground">{user?.email}</span>
        }
      />
      {team?.status === "ok" ? (
        <>
          <SettingsRow
            title={t("settings/organization_zmd6s2")}
            description={null}
            control={
              <span className="w-full text-right text-[13px] text-foreground">
                {team.team?.name ?? t("settings/personalOrganization_b7viei")}
              </span>
            }
          />
          {team.team ? (
            <SettingsRow
              title={t("settings/role_cn3jy1")}
              description={null}
              control={
                <span className="w-full text-right text-[13px] text-foreground">
                  {team.team.role === "admin"
                    ? t("settings/administrator_exo25v")
                    : t("settings/member_tlvrq7")}
                </span>
              }
            />
          ) : null}
        </>
      ) : null}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("settings/signOutOfYour_apqzmr")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("settings/newRequestsWillNot_tkcang")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogClose render={<Button variant="outline" />}>
              {t("common/cancel_8mt5fw")}
            </AlertDialogClose>
            <Button
              variant="destructive"
              disabled={isSigningOut}
              onClick={() => {
                void handleSignOut();
              }}
            >
              {t("settings/signOut_x1o1p3")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </SettingsSection>
  );
}
